import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import estimationsModule from '../../../components/estimations';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import timeTrackingModel from '$$timeTrackingModel';
import chartsConfig from './configCharts';
import app from '$$app';
import reportHelpers from '../include/helpers';
import numbersHelper from '../../../helpers/numbers';
import { reportWorkerError } from '$$workerErrorReporting';
import globalToast from '$$vueCmp/toast';
import chartsHelpers from '../include/charts/helpers';

const MONTH_LOCALES = [
  'report_month_jan',
  'report_month_feb',
  'report_month_march',
  'report_month_apr',
  'report_month_may',
  'report_month_jun',
  'report_month_jul',
  'report_month_aug',
  'report_month_sep',
  'report_month_oct',
  'report_month_nov',
  'report_month_dec',
];

const MONTH_IDS = ['jan', 'feb', 'march', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

class MonthBudgetReport {
  constructor() {
    this.costWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
    this.totalRange = {
      start: null,
      end: null,
    };
    this.baselineTotalRange = {
      start: null,
      end: null,
    };

    this.allResourcesMap = new Map();

    this.workerSettings = {};
  }

  init() {
    const allResourcesData = Store.getters['resourcesModel/getAllResources'];

    allResourcesData.forEach(resource => {
      if (!this.allResourcesMap.has(resource.id)) {
        this.allResourcesMap.set(resource.id, resource);
      }
    })

    this.initWorkerSettings(allResourcesData);
    this.initCostWorker();
  }

  destroy() {
    this.allResourcesMap.clear();
  }

  initWorkerSettings(allResourcesData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.workerSettings.projectConfigs = reportHelpers.prepareProjectConfigs();
    this.workerSettings.projectSettings = projectSettingsMap;
    this.workerSettings.customDaysData = customDayTemplatesModel.serialize();
    this.workerSettings.resourcesData = allResourcesData;
  }

  initCostWorker() {
    if (window.Worker && !this.costWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.costWorker = new Worker('/workers/costs_worker.js' + `?${version}`);
      this._initEvents();
    }
  }

  _initEvents() {
    const that = this;

    this.costWorker.onmessage = function (e) {
      const updatedCosts = e.data;

      Object.entries(updatedCosts).forEach(([key, updates]) => {
        if (updates.length) {
          const tasks = that.reportTasks[key]?.tasks;

          if (Array.isArray(tasks)) {
            const updatesMap = new Map(updates.map(obj => [obj.id, obj]));

            that.reportTasks[key].tasks = tasks.map(task => {
              const update = updatesMap.get(task.id);

              if (update) {
                return {
                  ...task,
                  cost: update.cost,
                  actual_cost: update.actual_cost,
                };
              }

              return task;
            });
          }
        }
      });

      that.calculateMonthBudgetReport();
    };

    this.costWorker.onerror = function (e) {
      reportWorkerError(e, 'costs_worker');
      globalToast.error(__('costs_calculating_error'));
    };
  }

  calculateCosts(tasks, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const defaultRange = reportHelpers.getDefaultRange().last_6_months;

    const totalEstimate = tasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);

    const tasksAndMilestones = tasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

    const range = {
      start: currentFilter?.properties?.rangeDate?.start || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || totalEstimate?.end_date || defaultRange.end,
    };

    const baselineRange = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || totalEstimate?.end_date || defaultRange.end,
    };

    this.totalRange = { ...range };
    this.baselineTotalRange = { ...baselineRange };

    const tasksLogsData = timeTrackingModel.getLogsByParams(totalEstimate.gantt_id);
    const projectData = projectsModel.getProjectDataById(totalEstimate.gantt_id);
    const projectTasksData = Store.getters['tasksModel/getItem'](totalEstimate.gantt_id);
    const { resourcesToTasks } = projectTasksData;

    const preapredTasks = this.prepareTasksForWorker(tasksAndMilestones, projectData, resourcesToTasks, tasksLogsData?.tasks);
    const preapredBaselineTasks = this.prepareTasksForWorker(baselineTasksAndMilestones, projectData, resourcesToTasks, tasksLogsData?.tasks);
    const filteredTasks = this.returnTasksByCurrentFilter(preapredTasks, currentFilter);
    const filteredBaselineTasks = this.returnTasksByCurrentFilter(preapredBaselineTasks, currentFilter);
    const tasksByMonth = this.prepareMonthTasks(filteredTasks, filteredBaselineTasks, range);

    const reportTasks = {
      filteredCurrentTasks: { tasks: filteredTasks, range },
      filteredBaselineTasks: { tasks: filteredBaselineTasks, range: baselineRange },
      ...tasksByMonth,
    };

    this.reportTasks = reportTasks;
    this.sendDataToWorker(reportTasks);
  }

  prepareMonthTasks(tasks, baselineTasks, range) {
    const startDate = moment(range.start);
    const endDate = moment(range.end);
    const startMonth = startDate.clone().startOf('month');
    const endMonth = endDate.clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');
    const preparedTasksMap = {};

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(range.start).add(i, 'months');
      let firstDayOfCurrentMonth = currentMonth.clone().startOf('month');
      let lastDayOfCurrentMonth = currentMonth.clone().endOf('month');

      if (i === 0) {
        firstDayOfCurrentMonth = moment(range.start);
      }

      if (i === monthsDifference) {
        lastDayOfCurrentMonth = moment(range.end);
      }

      const tasksByMonth = tasks.filter(task => reportHelpers.returnTaskByRange(
        {
          ...task,
          start_date: task.task_data.start_date,
          end_date: task.task_data.end_date,
        }, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth },
      ));
      const baselineTasksByMonth = baselineTasks.filter(task => reportHelpers.returnTaskByRange({
        ...task,
        start_date: task.task_data.start_date,
        end_date: task.task_data.end_date,
      }, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth }));

      const id = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;

      const workerRange = {
        start: firstDayOfCurrentMonth.valueOf(),
        end: lastDayOfCurrentMonth.valueOf(),
      };

      preparedTasksMap[id] = { tasks: tasksByMonth, range: workerRange };
      preparedTasksMap[`${id}-baseline`] = { tasks: baselineTasksByMonth, range: workerRange };
    }

    return preparedTasksMap;
  }

  sendDataToWorker(tasksData) {
    this.costWorker.postMessage(_.cloneDeep({
      projectConfigs: this.workerSettings.projectConfigs,
      projectSettings: this.workerSettings.projectSettings,
      customDaysData: this.workerSettings.customDaysData,
      resourcesData: this.workerSettings.resourcesData,
      tasksData,
    }));
  }

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => reportHelpers.returnTaskByRange({
      ...task,
      start_date: task.task_data.start_date,
      end_date: task.task_data.end_date,
    }, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resource_id.length) {
        return task;
      }

      const assigneeTask = task.resource_id.find(id => currentFilter.properties.assignee.includes(+id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      return task.task_data.text.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  }

  prepareTasksForWorker(tasks, projectData, resourcesToTasks, tasksLogs) {
    const resultData = [];

    _.each(tasks, taskData => {
      // const projectData = projectsModel.getProjectDataById(taskData.gantt_id);
      // const resources = Store.getters['resourcesModel/getResourcesByTaskIdAndGanttId'](taskData.gantt_id, taskData.id);
      // const timeLogsData = timeTrackingModel.getLogsByParams(taskData.gantt_id, taskData.id);
      const taskResources = resourcesToTasks[taskData.id] || [];
      const resources = taskResources.map(taskResource => this.allResourcesMap.get(taskResource.resource_id));
      const timeLogsData = tasksLogs[taskData.id];

      resultData.push({
        id: taskData.id,
        gantt_id: taskData.gantt_id,
        task_id: taskData.id, // do not remove
        project_name: projectData.name,
        task_name: taskData.text,
        task_data: taskData,
        info_text: taskData.note,
        resources,
        resource_id: _.map(resources, res => String(res.id)),
        resource_data: resources,
        time_logs: timeLogsData ? timeLogsData.sum : '0',
        cost: estimationsModule.calculateCostChildTask(taskData) || 0,
        actual_cost: estimationsModule.calculateActualCostChildTask(taskData) || 0,
        cost_range_start: taskData.start_date,
        cost_range_end: taskData.end_date,
        logsData: timeLogsData,
      });
    });

    return resultData;
  }

  calculateMonthBudgetReport() {
    if (!this.reportTasks?.filteredCurrentTasks?.tasks?.length) {
      app.trigger('onAfterMonthTimeReportCalculation', null);

      return;
    }

    const tableData = [];

    const isActualToBaseline = this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE;
    const isActualToPlanned = this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
    const isPlannedToBaseline = this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE

    let cumulativeCostResult = 0;
    let cumulativeBaselineCostResult = 0;
    let cumulativeActualCostResult = 0;

    const resultConfig = _.cloneDeep(chartsConfig.monthReport(this.calculationMode).default);
    const xAxisValues = [];

    const startMonth = moment(this.totalRange.start).clone().startOf('month');
    const endMonth = moment(this.totalRange.end).clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(this.totalRange.start).add(i, 'months');
      const dateString = `${MONTH_LOCALES[currentMonth.month()]} ${currentMonth.year()}`;

      xAxisValues.push(dateString);

      const dateKey = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;
      const baselineDateKey = `${dateKey}-baseline`;

      const monthLabel = dateString.split(' ');
      const tableMonthLabel = `${__(`${monthLabel[0]}_full`)} ${monthLabel[1]}`;

      if (isPlannedToBaseline) {
        const tasksByMonth = this.reportTasks[dateKey].tasks;
        const baselineTasksByMonth = this.reportTasks[baselineDateKey].tasks;

        const currentMonthCost = tasksByMonth.reduce((sum, task) => sum + +task.cost, 0);
        const baselineCurrentMonthCost = baselineTasksByMonth.reduce((sum, task) => sum + +task.cost, 0);

        cumulativeCostResult += currentMonthCost;
        cumulativeBaselineCostResult += baselineCurrentMonthCost;

        resultConfig.series = resultConfig.series.filter(item => !['actualCost', 'cumActualCost'].includes(item.name)).map(item => {
          if (item.name === 'totalCost') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: currentMonthCost, 
              hasLabel: true,
              type: 'cost',
            });
          }

          if (item.name === 'totalBaselineCost') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: baselineCurrentMonthCost, 
              hasLabel: true,
              type: 'cost',
            });
          }

          if (item.name === 'cumTotalCost') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeCostResult, 
              hasLabel: false,
              type: 'cost',
            });
          }

          if (item.name === 'cumBaselineTotalCost') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeBaselineCostResult, 
              hasLabel: false,
              type: 'cost',
            });
          }
        });

        tableData.push({
          month: tableMonthLabel,
          budget: currentMonthCost.toFixed(2),
          baseline_budget: baselineCurrentMonthCost.toFixed(2),
          actual_cost: 0,
          variance: (baselineCurrentMonthCost - currentMonthCost).toFixed(2),
          cum_budget: cumulativeCostResult.toFixed(2),
          cum_baseline_budget: cumulativeBaselineCostResult.toFixed(2),
          cum_actual_cost: 0,
          cum_variance: (cumulativeBaselineCostResult - cumulativeCostResult).toFixed(2),
        });
      }

      if (isActualToPlanned || isActualToBaseline) {
        const tasksByMonth = isActualToBaseline ? this.reportTasks[baselineDateKey].tasks : this.reportTasks[dateKey].tasks;
        const currentMonthCost = tasksByMonth.reduce((sum, task) => sum + +task.cost, 0);
        const currentMonthActualCost = tasksByMonth.reduce((sum, task) => sum + +task.actual_cost, 0);

        cumulativeCostResult += currentMonthCost;
        cumulativeActualCostResult += currentMonthActualCost;

        let seriesItems = resultConfig.series.filter(item => !['totalBaselineCost', 'cumBaselineTotalCost'].includes(item.name));

        if (isActualToBaseline) {
          seriesItems = resultConfig.series.filter(item => !['totalCost', 'cumTotalCost'].includes(item.name));
        }

        resultConfig.series = seriesItems.map(item => {
          if (item.name === 'totalCost' || item.name === 'totalBaselineCost') {
            if (isActualToBaseline && item.name === 'totalBaselineCost') {
              return chartsHelpers.prepareChartItemWithCalculatedValue({
                item, 
                value: currentMonthCost, 
                hasLabel: true, 
                color: '#8BC34A',
                type: 'cost',
              });
            }

            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: currentMonthCost, 
              hasLabel: true,
              type: 'cost',
            });
          }

          if (item.name === 'actualCost') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: currentMonthActualCost, 
              hasLabel: true,
              type: 'cost',
            });
          }

          if (item.name === 'cumTotalCost' || item.name === 'cumBaselineTotalCost') {
            if (isActualToBaseline && item.name === 'cumBaselineTotalCost') {
              return chartsHelpers.prepareChartItemWithCalculatedValue({
                item, 
                value: cumulativeCostResult, 
                hasLabel: false, 
                color: '#6E8F3F',
                type: 'cost',
              });
            }

            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeCostResult, 
              hasLabel: false,
              type: 'cost',
            });
          }

          if (item.name === 'cumActualCost') {
            return chartsHelpers.prepareChartItemWithCalculatedValue({
              item, 
              value: cumulativeActualCostResult, 
              hasLabel: false,
              type: 'cost',
            });
          }
        });

        tableData.push({
          month: tableMonthLabel,
          budget: currentMonthCost.toFixed(2),
          baseline_budget: currentMonthCost.toFixed(2),
          actual_cost: currentMonthActualCost.toFixed(2),
          variance: (currentMonthCost - currentMonthActualCost).toFixed(2),
          cum_budget: cumulativeCostResult.toFixed(2),
          cum_baseline_budget: cumulativeCostResult.toFixed(2),
          cum_actual_cost: cumulativeActualCostResult.toFixed(2),
          cum_variance: (cumulativeCostResult - cumulativeActualCostResult).toFixed(2),
        });
      }
    }

    const barValues = resultConfig.series.filter(item => item.type === 'bar').flatMap(item => item.data.map(obj => obj.value));
    const lineValues = resultConfig.series.filter(item => item.type === 'line').flatMap(item => item.data.map(obj => obj.value));
    const intervals = chartsHelpers.calculateYAxisIntervals(barValues, lineValues);

    if (intervals.left > 0 && intervals.right > 0) {
      resultConfig.xAxis[0].data = xAxisValues;
      resultConfig.yAxis[0].interval = intervals.left;
      resultConfig.yAxis[1].interval = intervals.right;

      app.trigger('onAfterMonthBubgetCalculation', {
        chart: resultConfig,
        table: tableData,
      });

      return;
    }

    app.trigger('onAfterMonthBubgetCalculation', null);
  }
}

const monthBudgetReport = new MonthBudgetReport();

export default monthBudgetReport;
